import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import Button from '../../components/UI/Button';
import { publicAPI } from '../../services/api';

type FormData = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

const ContactForm: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData & { attachment?: FileList }>();

  const selectedSubject = watch('subject');

  // Handle URL parameters to pre-fill form
  useEffect(() => {
    const urlSubject = searchParams.get('subject');
    const urlMessage = searchParams.get('message');

    if (urlSubject) {
      if (urlSubject.toLowerCase().includes('adopție') || urlSubject.toLowerCase().includes('adoption')) {
        setValue('subject', 'adoption');
      } else if (urlSubject === 'proiect-miau-casa') {
        setValue('subject', 'proiect-miau-casa');
      } else {
        setValue('subject', 'question');
      }
    }

    if (urlMessage) {
      setValue('message', decodeURIComponent(urlMessage));
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: FormData & { attachment?: FileList }) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      if (data.phone) formData.append('phone', data.phone);
      formData.append('subject', data.subject);
      formData.append('message', data.message);

      if (data.subject === 'proiect-miau-casa' && data.attachment && data.attachment.length > 0) {
        formData.append('attachment', data.attachment[0]);
      }

      await publicAPI.submitContact(formData);

      reset();
      setIsSubmitted(true);

      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError('A apărut o eroare la trimiterea mesajului. Te rugăm să încerci din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isSubmitted && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p>Mesajul tău a fost trimis cu succes! Te vom contacta în curând.</p>
        </div>
      )}

      {submitError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{submitError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="name" className="block text-gray-700 font-medium mb-1">Nume *</label>
          <input
            id="name"
            type="text"
            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Numele tău"
            {...register('name', { required: 'Numele este obligatoriu' })}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email *</label>
          <input
            id="email"
            type="email"
            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Email-ul tău"
            {...register('email', {
              required: 'Email-ul este obligatoriu',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Adresa de email este invalidă'
              }
            })}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">Telefon</label>
          <input
            id="phone"
            type="tel"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="Număr de telefon"
            {...register('phone')}
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-gray-700 font-medium mb-1">Subiect *</label>
          <select
            id="subject"
            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${errors.subject ? 'border-red-500' : 'border-gray-300'
              }`}
            {...register('subject', { required: 'Subiectul este obligatoriu' })}
          >
            <option value="">Selectează un subiect</option>
            <option value="adoption">Adopție</option>
            <option value="donation">Donație</option>
            <option value="volunteer">Voluntariat</option>
            <option value="proiect-miau-casa">Proiect Miau Casa</option>
            <option value="question">Întrebare generală</option>
            <option value="other">Altele</option>
          </select>
          {errors.subject && (
            <p className="text-red-500 text-sm mt-1">{errors.subject.message}</p>
          )}
        </div>
      </div>

      {selectedSubject === 'proiect-miau-casa' && (
        <div className="mb-4">
          <label htmlFor="attachment" className="block text-gray-700 font-medium mb-1">
            Încarcă Cererea Completată / Alte Documente
          </label>
          <input
            id="attachment"
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            {...register('attachment')}
          />
          <p className="text-xs text-gray-500 mt-1">Sunt acceptate fișiere PDF, DOCX, PNG, JPG.</p>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="message" className="block text-gray-700 font-medium mb-1">Mesaj *</label>
        <textarea
          id="message"
          rows={5}
          className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
          placeholder="Mesajul tău"
          {...register('message', { 
            required: 'Mesajul este obligatoriu',
            minLength: { value: 1, message: 'Mesajul trebuie să aibă cel puțin 1 caracter' }
          })}
        ></textarea>
        {errors.message && (
          <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
        )}
      </div>

      <div className="text-center">
        <Button
          type="submit"
          variant="accent"
          disabled={isSubmitting}
          className="w-full sm:w-auto min-w-[200px]"
        >
          {isSubmitting ? 'Se trimite...' : 'Trimite mesajul'}
        </Button>
        <p className="text-sm text-gray-500 mt-2">
          * Câmpuri obligatorii
        </p>
      </div>
    </form>
  );
};

export default ContactForm;